"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import "@assets/styles/footer.scss";
import type { Locale } from "@utils/i18n/config";

interface FooterPublicProps {
  locale?: Locale;
}

export default function FooterPublic({ locale = "de" }: FooterPublicProps) {
  const isEn = locale === "en";
  const homeHref = isEn ? "/en" : "/";
  const blogHref = isEn ? "/en/blog" : "/blog";
  const homeAriaLabel = isEn
    ? "DBI Analytics — Back to home"
    : "DBI Analytics — Zur Startseite";
  const companyDescription = isEn
    ? "DBI Analytics GmbH — Microsoft Solutions Partner for Data & AI. Power BI consulting, Microsoft Fabric migration, and Agentic BI for DACH enterprises."
    : "DBI Analytics GmbH — Microsoft Solutions Partner für Data & AI. Power BI Beratung, Microsoft Fabric Migration und Agentic BI für den DACH-Mittelstand und Konzerne.";
  const companySubline = isEn
    ? "Data & AI with European data sovereignty."
    : "Daten & KI mit europäischer Datensouveränität.";
  const servicesHeading = isEn ? "Services" : "Services";
  const powerBiConsultingLabel = isEn ? "Power BI Consulting" : "Power BI Beratung";
  const biConsultingLabel = isEn
    ? "Business Intelligence Consulting"
    : "Business Intelligence Beratung";
  const implementationLabel = isEn
    ? "Power BI Implementation"
    : "Power BI Implementierung";
  const migrationPricingLabel = isEn
    ? "Microsoft Fabric Migration"
    : "Microsoft Fabric Migration";
  const fixedPricePackagesLabel = isEn ? "Fixed-price packages" : "Festpreis-Pakete";
  const resourcesHeading = isEn ? "Resources" : "Ressourcen";
  const webinarsLabel = isEn ? "Webinars" : "Webinare";
  const glossaryLabel = isEn ? "Glossary" : "Glossar";
  const companyHeading = isEn ? "Company" : "Unternehmen";
  const aboutLabel = isEn ? "About DBI" : "Über DBI";
  const partnerLabel = isEn ? "Become a partner" : "Partner werden";
  const testimonialsLabel = isEn ? "Testimonials" : "Stimmen";
  const careersLabel = isEn ? "Careers" : "Stellenangebote";
  const pressLabel = isEn ? "Press releases" : "Pressemitteilungen";
  const bookCallLabel = isEn ? "Book a call" : "Termin buchen";
  const contactLabel = isEn ? "Contact" : "Kontakt";
  const rightsText = isEn
    ? "All rights reserved."
    : "Alle Rechte vorbehalten.";
  const legalNavLabel = isEn ? "Legal links" : "Rechtliche Links";
  const imprintLabel = isEn ? "Legal notice" : "Impressum";
  const privacyLabel = isEn ? "Privacy" : "Datenschutz";

  return (
    <footer
      className="footer"
      role="contentinfo"
      aria-label="DBI Analytics Footer"
    >

      <div className="container footer-container">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link
              href={homeHref}
              aria-label={homeAriaLabel}
              className="footer-brand-logo-link"
            >
              <Image
                src="/logo-wordmark-white-tight.svg"
                alt="DBI Analytics"
                width={226}
                height={69}
                unoptimized
                priority={false}
                className="footer-brand-logo"
              />
              {/*
                Hover layer: the same wordmark in its dark-surface colours
                (orange D, navy dot). It sits on top of the white logo and
                fades in on hover/focus, see footer.scss. Decorative only.
              */}
              <Image
                src="/logo-wordmark-white-tight-accent.svg"
                alt=""
                aria-hidden="true"
                width={226}
                height={69}
                unoptimized
                priority={false}
                className="footer-brand-logo footer-brand-logo--accent"
              />
            </Link>
            <p>
              {companyDescription}
            </p>
            <p className="footer-brand-subline">
              {companySubline}
            </p>
          </div>

          <div>
            <h5>{servicesHeading}</h5>
            <ul role="list">
              {/*
                Anchor text and target must match. These two previously pointed
                "Power BI Beratung" at /services/power-bi-implementierung and
                "BI Consultant" at the /services index — a mismatched signal on
                the strongest internal-link real estate there is (site-wide
                footer). Each anchor now links to the page it names.
              */}
              <li><Link href="/services/power-bi-beratung">{powerBiConsultingLabel}</Link></li>
              <li><Link href="/services/business-intelligence-beratung">{biConsultingLabel}</Link></li>
              <li><Link href="/services/power-bi-implementierung">{implementationLabel}</Link></li>
              <li><Link href="/services/bi-consultant">BI Consultant</Link></li>
              <li><Link href="/services/power-bi-migration-kosten">{migrationPricingLabel}</Link></li>
              <li><Link href="/services/managed-agentic-bi">Managed Agentic BI</Link></li>
              <li><Link href="/services/power-bi-mcp-server">Power BI MCP Server</Link></li>
              <li><Link href="/services#pricing">{fixedPricePackagesLabel}</Link></li>
            </ul>
          </div>

          <div>
            <h5>{resourcesHeading}</h5>
            <ul role="list">
              <li><Link href="/services/power-bi-schulung">{isEn ? "Power BI Training" : "Power BI Schulungen"}</Link></li>
              <li><Link href="/success-stories">Success Stories</Link></li>
              <li><Link href="/toolkit">Tools</Link></li>
              <li><Link href="/webinare">{webinarsLabel}</Link></li>
              <li><Link href={blogHref}>Blog</Link></li>
              <li><Link href="/blog/power-bi-glossar">{glossaryLabel}</Link></li>
              <li><Link href="/#faq">FAQ</Link></li>
            </ul>
          </div>

          <div>
            <h5>{companyHeading}</h5>
            <ul role="list">
              <li><Link href="/mission">{aboutLabel}</Link></li>
              <li><Link href="/partner-werden">{partnerLabel}</Link></li>
              <li><Link href="/#clients">Microsoft Partner</Link></li>
              <li><Link href="/#testimonials">{testimonialsLabel}</Link></li>
              {/*
                /pressemitteilungen was an orphan: in the sitemap, indexable,
                200 — but with zero internal links, so the crawler never
                reached it (Screaming Frog, 23.08.2026). The site-wide footer
                is the cheapest fix; it puts the page on every URL at once.
              */}
              <li><Link href="/pressemitteilungen">{pressLabel}</Link></li>
              <li><Link href="/jobs">{careersLabel}</Link></li>
              <li>
                <Link
                  href="https://calendly.com/hallo-dbi-analytics/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {bookCallLabel}
                </Link>
              </li>
              <li><Link href="/meet">{contactLabel}</Link></li>
            </ul>
          </div>
        </div>

        <div className="footer-legal">
          <span>© {new Date().getFullYear()} DBI Analytics GmbH · {rightsText}</span>
          <nav className="footer-legal-links" aria-label={legalNavLabel}>
            <Link href="/imprint">{imprintLabel}</Link>
            <Link href="/imprint#datenschutz">{privacyLabel}</Link>
          </nav>
        </div>
      </div>
    </footer>
  );
}
